import { KeyboardArrowRight } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useState, type ReactElement, type ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { useUserRoles } from 'app/hooks';
import ErrorState from 'app/shared-components/common/ErrorState';
import HealtheraSwitch from 'app/shared-components/forms/Switch';

import usePlusWebsites from '../../hooks/usePlusWebsites';
import { UrlEditModal } from '../../components/UrlEditModal';
import { validateExternalHomepageUrl } from '../../validation';
import {
  useOrganisationFeatureFlags,
  type FeatureFlagKey,
} from '../../hooks/useOrganisationFeatureFlags';
import { useUpdateOrganisationFeatureFlags } from './hooks/useUpdateOrganisationFeatureFlags';
import { useUpdateWebsiteDeployment } from './hooks/useUpdateWebsiteDeployment';
import { useUpdateWebsiteDomain } from './hooks/useUpdateWebsiteDomain';
import { useUpdateWebsiteExternalHomepageUrl } from './hooks/useUpdateWebsiteExternalHomepageUrl';
import { OnboardingJourneyModal } from './OnboardingJourneyModal';
import { useUpdateWebsiteOnboardingJourney } from './hooks/useUpdateWebsiteOnboardingJourney';
import { ONBOARDING_JOURNEY_OPTIONS } from './onboardingJourney';
import { AnalyticsSettingsModal } from './AnalyticsSettingsModal';
import { useUpdateWebsiteAnalyticSettings } from './hooks/useUpdateWebsiteAnalyticSettings';
import { SettingLabel } from './SettingLabel';
import {
  BESPOKE_TOOLTIP,
  DOMAIN_URL_TOOLTIP,
  EXTERNAL_HOMEPAGE_URL_TOOLTIP,
  GOOGLE_MEASUREMENT_ID_TOOLTIP,
  GOOGLE_TAG_MANAGER_ID_TOOLTIP,
  OnboardingJourneyTooltip,
  WEBSITE_STATUS_TOOLTIP,
  WEB_BUILDER_ACCESS_TOOLTIP,
} from './settingTooltips';

function SectionCard({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <Box className="vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white vtwo:p-6">
      <Typography className="vtwo:text-lg vtwo:font-bold vtwo:text-typography-primary vtwo:mb-4">
        {title}
      </Typography>
      {children}
    </Box>
  );
}

interface WebsiteStatusCardProps {
  isDeployed: boolean;
  isUpdating: boolean;
  onToggle: (isDeployed: boolean) => void;
}

function WebsiteStatusCard({
  isDeployed,
  isUpdating,
  onToggle,
}: WebsiteStatusCardProps): ReactElement {
  return (
    <SectionCard title="Website status">
      <Box className="vtwo:flex vtwo:items-center vtwo:justify-between">
        <SettingLabel label="Deployed/live" tooltip={WEBSITE_STATUS_TOOLTIP} />
        <Box data-testid="website-status-toggle">
          <HealtheraSwitch
            value={isDeployed}
            onChange={onToggle}
            disabled={isUpdating}
            disabledMessage="Website status update in progress"
          />
        </Box>
      </Box>
    </SectionCard>
  );
}

interface BuilderSettingsCardProps {
  webBuilderAccessEnabled: boolean;
  hasBespoke: boolean;
  isLoading: boolean;
  isUpdating: boolean;
  onToggle: (flag: FeatureFlagKey, value: boolean) => void;
}

function BuilderSettingsCard({
  webBuilderAccessEnabled,
  hasBespoke,
  isLoading,
  isUpdating,
  onToggle,
}: BuilderSettingsCardProps): ReactElement {
  return (
    <SectionCard title="Builder settings">
      {isLoading && (
        <Box className="vtwo:flex vtwo:justify-center">
          <CircularProgress size={20} />
        </Box>
      )}
      {!isLoading && (
        <Box className="vtwo:flex vtwo:flex-col vtwo:gap-4">
          <Box className="vtwo:flex vtwo:items-center vtwo:justify-between">
            <SettingLabel label="Web builder access" tooltip={WEB_BUILDER_ACCESS_TOOLTIP} />
            <Box data-testid="web-builder-access-toggle">
              <HealtheraSwitch
                value={webBuilderAccessEnabled}
                onChange={(value: boolean) => onToggle('webBuilderAccessEnabled', value)}
                disabled={isUpdating}
                disabledMessage="Builder settings update in progress"
              />
            </Box>
          </Box>
          <Box className="vtwo:flex vtwo:items-center vtwo:justify-between">
            <SettingLabel label="Bespoke" tooltip={BESPOKE_TOOLTIP} />
            <Box data-testid="bespoke-toggle">
              <HealtheraSwitch
                value={hasBespoke}
                onChange={(value: boolean) => onToggle('hasBespoke', value)}
                disabled={isUpdating}
                disabledMessage="Builder settings update in progress"
              />
            </Box>
          </Box>
        </Box>
      )}
    </SectionCard>
  );
}

interface UrlSettingCardProps {
  title: string;
  label: string;
  value: string | null;
  changeAriaLabel: string;
  tooltip?: ReactNode;
  onChange: () => void;
}

function UrlSettingCard({
  title,
  label,
  value,
  changeAriaLabel,
  tooltip,
  onChange,
}: UrlSettingCardProps): ReactElement {
  const labelNode = tooltip ? (
    <SettingLabel label={label} tooltip={tooltip} />
  ) : (
    <Typography className="vtwo:text-typography-primary">{label}</Typography>
  );

  return (
    <SectionCard title={title}>
      <Box className="vtwo:flex vtwo:items-start vtwo:justify-between">
        <Box>
          {labelNode}
          {value && (
            <Typography variant="body2" className="vtwo:text-gray-500 vtwo:mt-1 vtwo:break-all">
              {value}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          endIcon={<KeyboardArrowRight />}
          onClick={onChange}
          aria-label={changeAriaLabel}
        >
          Change
        </Button>
      </Box>
    </SectionCard>
  );
}

interface AnalyticsSettingsCardProps {
  googleTagManagerId: string | null;
  googleMeasurementId: string | null;
  onChange: () => void;
}

function AnalyticsSettingsCard({
  googleTagManagerId,
  googleMeasurementId,
  onChange,
}: AnalyticsSettingsCardProps): ReactElement {
  return (
    <SectionCard title="Analytics">
      <Box className="vtwo:flex vtwo:items-start vtwo:justify-between">
        <Box className="vtwo:flex vtwo:flex-col vtwo:gap-2">
          <Box>
            <SettingLabel label="Google Tag Manager ID" tooltip={GOOGLE_TAG_MANAGER_ID_TOOLTIP} />
            {googleTagManagerId && (
              <Typography variant="body2" className="vtwo:text-gray-500 vtwo:mt-1 vtwo:break-all">
                {googleTagManagerId}
              </Typography>
            )}
          </Box>
          <Box>
            <SettingLabel label="Google Measurement ID" tooltip={GOOGLE_MEASUREMENT_ID_TOOLTIP} />
            {googleMeasurementId && (
              <Typography variant="body2" className="vtwo:text-gray-500 vtwo:mt-1 vtwo:break-all">
                {googleMeasurementId}
              </Typography>
            )}
          </Box>
        </Box>
        <Button
          size="small"
          endIcon={<KeyboardArrowRight />}
          onClick={onChange}
          aria-label="Change analytics settings"
        >
          Change
        </Button>
      </Box>
    </SectionCard>
  );
}

type EditableField = 'domain' | 'externalHomepageUrl' | 'onboardingJourney' | 'analytics';

const AdminSettingsTab = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const { isSupport } = useUserRoles();
  const { plusWebsites, isLoading, isError } = usePlusWebsites();
  const updateDeployment = useUpdateWebsiteDeployment();
  const updateDomain = useUpdateWebsiteDomain();
  const updateExternalHomepageUrl = useUpdateWebsiteExternalHomepageUrl();
  const updateOnboardingJourney = useUpdateWebsiteOnboardingJourney();
  const updateAnalyticSettings = useUpdateWebsiteAnalyticSettings();
  const { featureFlags, isLoading: isFlagsLoading } = useOrganisationFeatureFlags();
  const updateFeatureFlags = useUpdateOrganisationFeatureFlags();
  const organisationId = searchParams.get('organisationId') ?? '';
  const [editingField, setEditingField] = useState<EditableField | null>(null);

  if (!isSupport) {
    return (
      <Navigate to={{ pathname: '/plus-websites', search: searchParams.toString() }} replace />
    );
  }

  if (isError) {
    return (
      <Box className="vtwo:px-16 vtwo:pt-8 vtwo:pb-16">
        <ErrorState title="Failed to load admin settings" subtitle="Try again later" />
      </Box>
    );
  }

  const website = plusWebsites[0] ?? null;

  if (isLoading || !website) {
    return (
      <Box className="vtwo:px-16 vtwo:py-24 vtwo:flex vtwo:justify-center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  const closeModal = () => setEditingField(null);

  return (
    <Box className="vtwo:px-16 vtwo:pt-8 vtwo:pb-16">
      <Box className="vtwo:max-w-2xl vtwo:mx-auto vtwo:flex vtwo:flex-col vtwo:gap-6">
        <Typography className="vtwo:text-lg vtwo:font-bold vtwo:text-typography-primary">
          Admin settings
        </Typography>
        <BuilderSettingsCard
          webBuilderAccessEnabled={featureFlags?.webBuilderAccessEnabled ?? false}
          hasBespoke={featureFlags?.hasBespoke ?? false}
          isLoading={isFlagsLoading}
          isUpdating={updateFeatureFlags.isLoading}
          onToggle={(flag, value) => updateFeatureFlags.mutate({ organisationId, flag, value })}
        />
        <WebsiteStatusCard
          isDeployed={website.isDeployed}
          isUpdating={updateDeployment.isLoading}
          onToggle={(isDeployed) => updateDeployment.mutate({ websiteId: website.id, isDeployed })}
        />
        <UrlSettingCard
          title="Domain"
          label="Domain URL"
          value={website.domain}
          changeAriaLabel="Change domain URL"
          tooltip={DOMAIN_URL_TOOLTIP}
          onChange={() => setEditingField('domain')}
        />
        <UrlSettingCard
          title="Home page"
          label="External homepage URL"
          value={website.externalHomepageUrl ?? null}
          changeAriaLabel="Change external homepage URL"
          tooltip={EXTERNAL_HOMEPAGE_URL_TOOLTIP}
          onChange={() => setEditingField('externalHomepageUrl')}
        />
        <UrlSettingCard
          title="Patient onboarding journey"
          label="Journey type"
          value={
            ONBOARDING_JOURNEY_OPTIONS.find((option) => option.value === website.onboardingJourney)
              ?.label ?? null
          }
          tooltip={<OnboardingJourneyTooltip />}
          changeAriaLabel="Change onboarding journey"
          onChange={() => setEditingField('onboardingJourney')}
        />
        <AnalyticsSettingsCard
          googleTagManagerId={website.googleTagManagerId ?? null}
          googleMeasurementId={website.googleMeasurementId ?? null}
          onChange={() => setEditingField('analytics')}
        />
      </Box>

      <UrlEditModal
        open={editingField === 'domain'}
        title="Domain URL"
        initialValue={website.domain ?? ''}
        isSaving={updateDomain.isLoading}
        onClose={closeModal}
        onSave={(domain) =>
          updateDomain.mutate({ websiteId: website.id, domain }, { onSuccess: closeModal })
        }
      />
      <UrlEditModal
        open={editingField === 'externalHomepageUrl'}
        title="External homepage URL"
        initialValue={website.externalHomepageUrl ?? ''}
        isSaving={updateExternalHomepageUrl.isLoading}
        allowEmpty
        hint="Leave empty to remove the external homepage URL"
        validate={validateExternalHomepageUrl}
        onClose={closeModal}
        onSave={(url) =>
          updateExternalHomepageUrl.mutate(
            { websiteId: website.id, externalHomepageUrl: url || null },
            { onSuccess: closeModal }
          )
        }
      />
      <OnboardingJourneyModal
        open={editingField === 'onboardingJourney'}
        initialValue={website.onboardingJourney ?? null}
        isSaving={updateOnboardingJourney.isLoading}
        onClose={closeModal}
        onSave={(onboardingJourney) =>
          updateOnboardingJourney.mutate(
            { websiteId: website.id, onboardingJourney },
            { onSuccess: closeModal }
          )
        }
      />
      <AnalyticsSettingsModal
        open={editingField === 'analytics'}
        initialValues={{
          googleTagManagerId: website.googleTagManagerId ?? '',
          googleMeasurementId: website.googleMeasurementId ?? '',
        }}
        isSaving={updateAnalyticSettings.isLoading}
        onClose={closeModal}
        onSave={({ googleTagManagerId, googleMeasurementId }) =>
          updateAnalyticSettings.mutate(
            {
              websiteId: website.id,
              googleTagManagerId: googleTagManagerId || null,
              googleMeasurementId: googleMeasurementId || null,
            },
            { onSuccess: closeModal }
          )
        }
      />
    </Box>
  );
};

export default AdminSettingsTab;
